import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Send, Loader2, Paperclip } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable composer to email a generated staff document (PDF attached).
 * The backend resolves the employee's email from the document/employee and
 * CC's the HR mailbox. HR may edit the subject/message before sending.
 */
const EmailDocDialog = ({
  open,
  onOpenChange,
  title = 'Email document to staff',
  recipientName,
  attachmentLabel = 'document',
  payload,
  defaultSubject = '',
  defaultMessage = '',
}) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setMessage(defaultMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/document-email/send`, {
        ...payload,
        subject: subject || undefined,
        message: message || undefined,
      });
      toast({ title: 'Email sent', description: res.data?.message || 'Document emailed.' });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Could not send',
        description: err?.response?.data?.detail || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="email-doc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Sends to {recipientName ? <span className="font-medium text-gray-700">{recipientName}</span> : 'the employee on file'}
            {' '}with a copy (CC) to HR. The {attachmentLabel} PDF is attached.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="doc-email-subject">Subject</Label>
            <Input
              id="doc-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="(default subject will be used if left blank)"
              disabled={sending}
              data-testid="email-doc-subject"
            />
          </div>
          <div>
            <Label htmlFor="doc-email-message">Message</Label>
            <Textarea
              id="doc-email-message"
              rows={7}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="(default message will be used if left blank)"
              disabled={sending}
              data-testid="email-doc-message"
            />
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <Paperclip className="h-3.5 w-3.5" /> The {attachmentLabel} is attached automatically as a PDF.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="email-doc-cancel">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending} data-testid="email-doc-send">
            {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-2" /> Send email</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDocDialog;
